/**
 * 一時保留・呼び戻しコンポーネント
 */
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Alert,
  Snackbar,
  Divider,
  Chip,
  CircularProgress,
  Badge,
  Card,
  CardContent,
  CardActions,
} from "@mui/material";
import {
  Pause as HoldIcon,
  PlayArrow as RecallIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Note as NoteIcon,
  ShoppingCart as CartIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material";
import api from "../../services/api";

const HeldTransactions = ({
  currentItems = [],
  onHold,
  onRecall,
  compact = false,
}) => {
  const { storeId } = useParams();
  const [heldTransactions, setHeldTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);

  // ダイアログ状態
  const [holdDialog, setHoldDialog] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // フォーム状態
  const [label, setLabel] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [note, setNote] = useState("");

  // スナックバー
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchHeldTransactions();
    // 30秒ごとに更新
    const interval = setInterval(fetchHeldTransactions, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const fetchHeldTransactions = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/held-transactions/${storeId}`);
      setHeldTransactions(response.data.transactions || []);
    } catch (err) {
      setError("保留取引の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleHoldTransaction = async () => {
    if (!currentItems || currentItems.length === 0) {
      setSnackbar({
        open: true,
        message: "保留する商品がありません",
        severity: "warning",
      });
      return;
    }

    try {
      const response = await api.post(`/held-transactions/${storeId}`, {
        items: currentItems,
        label: label || undefined,
        customer: customerName ? { name: customerName } : undefined,
        note: note || undefined,
      });

      await fetchHeldTransactions();
      setHoldDialog(false);
      setLabel("");
      setCustomerName("");
      setNote("");

      setSnackbar({
        open: true,
        message: response.data.message || "取引を保留しました",
        severity: "success",
      });

      if (onHold) {
        onHold(response.data.transaction);
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "保留に失敗しました",
        severity: "error",
      });
    }
  };

  const handleRecallTransaction = async (transactionId) => {
    try {
      const response = await api.post(
        `/held-transactions/${storeId}/recall/${transactionId}`
      );

      await fetchHeldTransactions();
      setDetailDialog(false);
      setSelectedTransaction(null);

      setSnackbar({
        open: true,
        message: "取引を呼び戻しました",
        severity: "success",
      });

      if (onRecall) {
        onRecall(response.data.transaction);
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "呼び戻しに失敗しました",
        severity: "error",
      });
    }
  };

  const handleCancelTransaction = async (transactionId) => {
    if (!window.confirm("この保留取引をキャンセルしますか？")) {
      return;
    }

    try {
      await api.delete(`/held-transactions/${storeId}/${transactionId}`);

      await fetchHeldTransactions();
      setDetailDialog(false);
      setSelectedTransaction(null);

      setSnackbar({
        open: true,
        message: "保留取引をキャンセルしました",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "キャンセルに失敗しました",
        severity: "error",
      });
    }
  };

  const handleUpdateTransaction = async () => {
    if (!selectedTransaction) return;

    try {
      await api.patch(
        `/held-transactions/${storeId}/${selectedTransaction.id}`,
        {
          label,
          customer: customerName ? { name: customerName } : undefined,
          note,
        }
      );

      await fetchHeldTransactions();
      setEditDialog(false);

      setSnackbar({
        open: true,
        message: "保留取引を更新しました",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "更新に失敗しました",
        severity: "error",
      });
    }
  };

  const openDetailDialog = (transaction) => {
    setSelectedTransaction(transaction);
    setDetailDialog(true);
  };

  const openEditDialog = (transaction) => {
    setSelectedTransaction(transaction);
    setLabel(transaction.label || "");
    setCustomerName(transaction.customer?.name || "");
    setNote(transaction.note || "");
    setEditDialog(true);
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);

    if (diffMins < 1) return "たった今";
    if (diffMins < 60) return `${diffMins}分前`;
    if (diffHours < 24) return `${diffHours}時間前`;
    return date.toLocaleDateString("ja-JP");
  };

  // コンパクト表示
  if (compact) {
    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Badge badgeContent={heldTransactions.length} color="primary">
            <HoldIcon />
          </Badge>
          <Typography variant="subtitle2">保留中</Typography>
          <IconButton size="small" onClick={fetchHeldTransactions}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>

        {heldTransactions.length > 0 ? (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {heldTransactions.slice(0, 5).map((t) => (
              <Chip
                key={t.id}
                label={`#${t.holdNumber} ${t.label || ""} ¥${(t.totalAmount || 0).toLocaleString()}`}
                onClick={() => openDetailDialog(t)}
                onDelete={() => handleRecallTransaction(t.id)}
                deleteIcon={<RecallIcon />}
                variant="outlined"
                size="small"
              />
            ))}
            {heldTransactions.length > 5 && (
              <Chip
                label={`+${heldTransactions.length - 5}件`}
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="textSecondary">
            保留中の取引はありません
          </Typography>
        )}

        {currentItems && currentItems.length > 0 && (
          <Button
            size="small"
            startIcon={<HoldIcon />}
            onClick={() => setHoldDialog(true)}
            sx={{ mt: 1 }}
          >
            現在の取引を保留
          </Button>
        )}

        {/* ダイアログ類（共通） */}
        {renderDialogs()}
      </Box>
    );
  }

  // フル表示
  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h5">
            <HoldIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            一時保留取引
          </Typography>
          <Box>
            <IconButton onClick={fetchHeldTransactions} title="更新">
              <RefreshIcon />
            </IconButton>
            {currentItems && currentItems.length > 0 && (
              <Button
                variant="contained"
                startIcon={<HoldIcon />}
                onClick={() => setHoldDialog(true)}
              >
                現在の取引を保留
              </Button>
            )}
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : heldTransactions.length === 0 ? (
          <Alert severity="info">保留中の取引はありません</Alert>
        ) : (
          <Grid container spacing={2}>
            {heldTransactions.map((transaction) => (
              <Grid item xs={12} sm={6} md={4} key={transaction.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                      <Chip
                        label={`#${transaction.holdNumber}`}
                        color="primary"
                        size="small"
                      />
                      <Typography variant="body2" color="textSecondary">
                        <TimeIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }} />
                        {formatTimeAgo(transaction.heldAt)}
                      </Typography>
                    </Box>

                    {transaction.label && (
                      <Typography variant="subtitle1" gutterBottom>
                        {transaction.label}
                      </Typography>
                    )}

                    {transaction.customer?.name && (
                      <Typography variant="body2" color="textSecondary">
                        <PersonIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }} />
                        {transaction.customer.name}
                      </Typography>
                    )}

                    <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                      ¥{(transaction.totalAmount || 0).toLocaleString()}
                    </Typography>

                    <Typography variant="body2" color="textSecondary">
                      <CartIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }} />
                      {transaction.items?.length || 0}点
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={<RecallIcon />}
                      onClick={() => handleRecallTransaction(transaction.id)}
                    >
                      呼び戻す
                    </Button>
                    <Button
                      size="small"
                      onClick={() => openDetailDialog(transaction)}
                    >
                      詳細
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => openEditDialog(transaction)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleCancelTransaction(transaction.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {renderDialogs()}
    </Box>
  );

  function renderDialogs() {
    return (
      <>
        {/* 保留ダイアログ */}
        <Dialog open={holdDialog} onClose={() => setHoldDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>取引を保留</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              現在の取引（{currentItems?.length || 0}点）を一時保留します
            </Typography>
            <TextField
              fullWidth
              label="ラベル（任意）"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="例: テーブル5、田中様"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="お客様名（任意）"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="メモ（任意）"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
              rows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHoldDialog(false)}>キャンセル</Button>
            <Button variant="contained" onClick={handleHoldTransaction}>
              保留する
            </Button>
          </DialogActions>
        </Dialog>

        {/* 詳細ダイアログ */}
        <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            保留取引 #{selectedTransaction?.holdNumber}
          </DialogTitle>
          <DialogContent>
            {selectedTransaction && (
              <Box>
                {selectedTransaction.label && (
                  <Typography variant="h6" gutterBottom>
                    {selectedTransaction.label}
                  </Typography>
                )}

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">保留時刻</Typography>
                    <Typography>
                      {new Date(selectedTransaction.heldAt).toLocaleString("ja-JP")}
                    </Typography>
                  </Grid>
                  {selectedTransaction.customer?.name && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">お客様</Typography>
                      <Typography>{selectedTransaction.customer.name}</Typography>
                    </Grid>
                  )}
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>商品一覧</Typography>
                <List dense>
                  {selectedTransaction.items?.map((item, idx) => (
                    <ListItem key={idx}>
                      <ListItemText
                        primary={item.name}
                        secondary={`¥${(item.price || 0).toLocaleString()} × ${item.quantity || 1}`}
                      />
                      <Typography>
                        ¥{((item.price || 0) * (item.quantity || 1)).toLocaleString()}
                      </Typography>
                    </ListItem>
                  ))}
                </List>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h5" align="right">
                  合計: ¥{(selectedTransaction.totalAmount || 0).toLocaleString()}
                </Typography>

                {selectedTransaction.note && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <NoteIcon sx={{ fontSize: 14, mr: 1, verticalAlign: "middle" }} />
                    {selectedTransaction.note}
                  </Alert>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              color="error"
              onClick={() => handleCancelTransaction(selectedTransaction?.id)}
            >
              キャンセル
            </Button>
            <Button onClick={() => setDetailDialog(false)}>閉じる</Button>
            <Button
              variant="contained"
              startIcon={<RecallIcon />}
              onClick={() => handleRecallTransaction(selectedTransaction?.id)}
            >
              呼び戻す
            </Button>
          </DialogActions>
        </Dialog>

        {/* 編集ダイアログ */}
        <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>保留取引を編集</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="ラベル"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              sx={{ mb: 2, mt: 1 }}
            />
            <TextField
              fullWidth
              label="お客様名"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="メモ"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
              rows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialog(false)}>キャンセル</Button>
            <Button variant="contained" onClick={handleUpdateTransaction}>
              更新
            </Button>
          </DialogActions>
        </Dialog>

        {/* スナックバー */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  }
};

export default HeldTransactions;
